import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Accordion } from '~/eds/src/components/Accordion/Accordion';
import { Button } from '~/eds/src/components/Button/Button';
import { CheckboxGroup } from '~/eds/src/components/CheckboxGroup/CheckboxGroup';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

export const ClauseTaggingAccessSetting = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { params } = useRouting();
  const { clientId } = params;
  const { toast } = useToast();
  const configs = useSelector(
    slices.moreSettings.selectors.selectConfiguration,
  );

  const [updateClientAdmin, { isLoading: isUpdateLoading }] =
    api.endpoints.updateClientAdmin.useMutation({});

  const disableDocTagging =
    configs.clauseTagsAccessMode.includes('restrict_all_users');

  return (
    <Accordion
      isIndented
      border="all"
      padding="all"
      itemGap="s"
      items={[
        {
          title: intl.formatMessage({
            id: 'ClauseTaggingAccessSetting.clauseQuickAiTitle',
            defaultMessage: 'Clause Quick AI',
            description: 'Title for the Clause Quick AI accordion section',
          }),
          isBordered: true,
          isExpanded: true,
          headerBorder: 'bottom',
          content: (
            <Layout preset="sections" pt={6} align="flex-start">
              <CheckboxGroup
                name="deleted-documents"
                value={configs.clauseTagsAccessMode}
                options={[
                  {
                    label: intl.formatMessage({
                      id: 'ClauseTaggingAccessSetting.restrictAllUsersLabel',
                      defaultMessage:
                        'Restrict all users from applying clause tags to all documents from the Document Viewer.',
                      description:
                        'Checkbox label for restricting clause tagging access for all users',
                    }),
                    value: 'restrict_all_users',
                  },
                ]}
                onChange={(mode) => {
                  dispatch(
                    slices.moreSettings.actions.setConfigs({
                      ...configs,
                      clauseTagsAccessMode: mode ? mode : [],
                    }),
                  );
                }}
              />

              <Button
                label="save-enable-clause"
                text={intl.formatMessage({
                  id: 'ClauseTaggingAccessSetting.saveChangesButton',
                  defaultMessage: 'Save Changes',
                  description: 'Button text for saving clause tagging changes',
                })}
                variant="primary"
                isLoading={isUpdateLoading}
                onClick={() => {
                  updateClientAdmin({
                    clientId: clientId!,
                    disableDocTagging,
                  })
                    .then(() => {
                      toast({
                        message: disableDocTagging
                          ? intl.formatMessage({
                              id: 'ClauseTaggingAccessSetting.disableSuccessMessage',
                              defaultMessage:
                                'Clause tagging has been disabled.',
                              description:
                                'Success message when clause tagging setting is disabled',
                            })
                          : intl.formatMessage({
                              id: 'ClauseTaggingAccessSetting.enableSuccessMessage',
                              defaultMessage:
                                'Clause tagging has been enabled.',
                              description:
                                'Success message when clause tagging setting is enabled',
                            }),
                        status: 'success',
                      });
                    })
                    .catch(() => {
                      toast({
                        message: disableDocTagging
                          ? intl.formatMessage({
                              id: 'ClauseTaggingAccessSetting.disableErrorMessage',
                              defaultMessage:
                                'An error occurred while disabling clause tagging.',
                              description:
                                'Error message when disabling clause tagging fails',
                            })
                          : intl.formatMessage({
                              id: 'ClauseTaggingAccessSetting.enableErrorMessage',
                              defaultMessage:
                                'An error occurred while enabling clause tagging.',
                              description:
                                'Error message when enabling clause tagging fails',
                            }),
                        status: 'danger',
                      });
                    });
                }}
              />
            </Layout>
          ),
        },
      ]}
    />
  );
};
